import { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, TextField, CircularProgress,
  Divider, Stack, Chip,
} from '@mui/material';
import TrendingUpIcon    from '@mui/icons-material/TrendingUp';
import TrendingDownIcon  from '@mui/icons-material/TrendingDown';
import authFetch from '../utils/authFetch';
import { GOLD, typo } from '../styles/ds';

const fmt = n => `₪${Number(n || 0).toLocaleString('he-IL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const StatCard = ({ label, value, color, sub }) => (
  <Card sx={{ borderRadius: 2, flex: 1 }}>
    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography fontWeight={700} fontSize={18} color={color || 'text.primary'}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </CardContent>
  </Card>
);

const ProfitReport = () => {
  const now   = new Date();
  const defMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const [month, setMonth]     = useState(defMonth);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const load = (m) => {
    setLoading(true);
    authFetch(`/profit-report${m ? `?month=${m}` : ''}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(month); }, [month]);

  const rows   = data?.rows   || [];
  const totals = data?.totals || {};

  return (
    <Box className="crm-page" p={3}>
      <Stack className="crm-header crm-filter-row" direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography {...typo.pageTitle}>דוח רווחיות</Typography>
        <TextField
          type="month"
          size="small"
          value={month}
          onChange={e => setMonth(e.target.value)}
          label="חודש"
          InputLabelProps={{ shrink: true }}
          sx={{ width: 180 }}
        />
      </Stack>

      <Typography variant="caption" color="text.secondary" display="block" mb={2}>
        * נתוני רווחיות עבור הזמנות שסופקו לפני 07/2026 חלקיים — עלויות ללא מקור מתועד לא שוחזרו (החלטה עסקית, ראו sql_db/README.md)
      </Typography>

      {/* Summary cards */}
      <Stack className="crm-wrap crm-stat-row" direction="row" spacing={2} mb={3} flexWrap="wrap">
        <StatCard label="הכנסות מכירה"   value={fmt(totals.revenue_ils)}         color="#2e7d32" />
        <StatCard label="עלויות רכישה"   value={fmt(totals.purchase_cost_ils)}   color="#c62828" />
        <StatCard label="עלויות ייצור"   value={fmt(totals.production_cost_ils)} color="#e65100" />
        <StatCard label={'סה"כ הוצאות'}   value={fmt(totals.total_expenses_ils)}  color="#721c24" />
        <StatCard
          label="רווח נקי"
          value={fmt(totals.net_profit_ils)}
          color={Number(totals.net_profit_ils) >= 0 ? '#2e7d32' : '#c62828'}
          sub={Number(totals.qty_sold) > 0 ? `${totals.qty_sold} פריטים נמכרו` : ''}
        />
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Box textAlign="center" py={8}>
          <Typography color="text.secondary">אין נתונים לחודש זה</Typography>
        </Box>
      ) : (
        <TableContainer component={Box} sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
          <Box className="crm-table-scroll crm-record-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="דוח רווחיות"><Table className="crm-record-table" size="small">
            <TableHead>
              <TableRow>
                <TableCell>מוצר</TableCell>
                <TableCell>קטגוריה</TableCell>
                <TableCell align="center">כמות נמכרה</TableCell>
                <TableCell align="right">הכנסות</TableCell>
                <TableCell align="right">עלויות רכישה</TableCell>
                <TableCell align="right">עלויות ייצור</TableCell>
                <TableCell align="right">סה"כ הוצאות</TableCell>
                <TableCell align="right">רווח נקי</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => {
                const profit = Number(r.net_profit_ils);
                const isPos  = profit >= 0;
                return (
                  <TableRow className="crm-record-row" key={r.variant_id} sx={{ backgroundColor: i % 2 === 0 ? '#fff' : '#faf8f5' }}>
                    <TableCell className="crm-record-title crm-record-full" sx={{ fontWeight: 600, maxWidth: 200 }}>{r.variant_name || `וריאנט #${r.variant_id}`}</TableCell>
                    <TableCell className="crm-record-full" data-label="קטגוריה" sx={{ color: 'text.secondary', fontSize: 12 }}>{r.product_name || '—'}</TableCell>
                    <TableCell data-label="כמות נמכרה" align="center" sx={{ fontWeight: 600 }}>{r.qty_sold}</TableCell>
                    <TableCell data-label="הכנסות" align="right" sx={{ color: '#2e7d32', fontWeight: 600 }}>{fmt(r.revenue_ils)}</TableCell>
                    <TableCell data-label="עלויות רכישה" align="right" sx={{ color: '#c62828' }}>{fmt(r.purchase_cost_ils)}</TableCell>
                    <TableCell data-label="עלויות ייצור" align="right" sx={{ color: '#e65100' }}>{fmt(r.production_cost_ils)}</TableCell>
                    <TableCell data-label="סה״כ הוצאות" align="right" sx={{ color: '#721c24' }}>{fmt(r.total_expenses_ils)}</TableCell>
                    <TableCell data-label="רווח נקי" align="right">
                      <Chip
                        icon={isPos ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
                        label={fmt(profit)}
                        size="small"
                        sx={{
                          backgroundColor: isPos ? '#D4EDDA' : '#F8D7DA',
                          color:           isPos ? '#155724' : '#721C24',
                          fontWeight: 700, fontSize: 12,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}

              {/* Totals row */}
              <TableRow className="crm-record-row crm-record-total" sx={{ backgroundColor: '#f0ece3', fontWeight: 700 }}>
                <TableCell className="crm-record-title crm-record-full" sx={{ fontWeight: 700 }} colSpan={2}>סה"כ</TableCell>
                <TableCell data-label="כמות נמכרה" align="center" sx={{ fontWeight: 700 }}>{totals.qty_sold}</TableCell>
                <TableCell data-label="הכנסות" align="right" sx={{ fontWeight: 700, color: '#2e7d32' }}>{fmt(totals.revenue_ils)}</TableCell>
                <TableCell data-label="עלויות רכישה" align="right" sx={{ fontWeight: 700, color: '#c62828' }}>{fmt(totals.purchase_cost_ils)}</TableCell>
                <TableCell data-label="עלויות ייצור" align="right" sx={{ fontWeight: 700, color: '#e65100' }}>{fmt(totals.production_cost_ils)}</TableCell>
                <TableCell data-label="סה״כ הוצאות" align="right" sx={{ fontWeight: 700, color: '#721c24' }}>{fmt(totals.total_expenses_ils)}</TableCell>
                <TableCell data-label="רווח נקי" align="right">
                  <Typography fontWeight={800} fontSize={14} color={Number(totals.net_profit_ils) >= 0 ? '#155724' : '#721c24'}>
                    {fmt(totals.net_profit_ils)}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table></Box>
        </TableContainer>
      )}
    </Box>
  );
};

export default ProfitReport;
